import { motion } from "framer-motion";
import { HOUSE } from "@/constants/testIds";

/**
 * Editorial "One Platform. Many Environments." — typography-first.
 * No fake installations, no stock photos. Two columns of environment
 * families with the same Abike concept threading through each.
 */

const ENVIRONMENTS = [
  {
    name: "Hospitality",
    contexts: ["Hotels", "Resorts", "Guest suites", "Concierge desks"],
  },
  {
    name: "Fine Dining",
    contexts: ["Restaurants", "Chef's tables", "Private dining rooms"],
  },
  {
    name: "Restrooms",
    contexts: [
      "Public and commercial restrooms",
      "Executive washrooms",
      "Powder rooms",
    ],
  },
  {
    name: "Retail",
    contexts: ["Fitting rooms", "Flagship stores", "Consignment houses"],
  },
  {
    name: "Airports & Lounges",
    contexts: ["Terminals", "First-class lounges", "Private-aviation FBOs"],
  },
  {
    name: "Healthcare",
    contexts: [
      "Examination rooms",
      "Consultation suites",
      "Wellness clinics",
    ],
  },
  {
    name: "Spas & Wellness",
    contexts: ["Treatment rooms", "Locker areas", "Meditation studios"],
  },
  {
    name: "Corporate",
    contexts: ["Executive offices", "Boardrooms", "Client suites"],
  },
  {
    name: "Residential & Development",
    contexts: [
      "Custom homes",
      "Luxury developments",
      "Foyers and entryways",
    ],
  },
];

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
};

export const Environments = () => {
  return (
    <section
      id="environments"
      data-testid={HOUSE.sectionEnvironments}
      className="relative bg-[var(--black)] py-28 md:py-40 border-t border-[var(--border-dark)]"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-12 gap-10 mb-24">
          <motion.div {...fadeUp} className="md:col-span-6">
            <p className="font-sans text-[11px] uppercase tracking-[0.5em] text-[var(--gold-light)] mb-8">
              One Platform · Many Environments
            </p>
            <h2 className="font-serif text-[var(--ivory)] text-5xl sm:text-6xl lg:text-[5.5rem] leading-[1.02] tracking-tight font-light">
              The same object.
              <br />
              <span className="italic text-[var(--stone)]">
                Considered anywhere.
              </span>
            </h2>
          </motion.div>

          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.1 }}
            className="md:col-span-5 md:col-start-8 flex items-end"
          >
            <p className="font-sans text-[var(--stone)] text-base md:text-lg leading-relaxed">
              Abike is a single architectural platform. Its materials, finishes,
              and integration can be considered for the room it lives in —
              while its silhouette, mechanism, and philosophy remain the same.
              <span className="block mt-4 text-[var(--muted-dark)] text-sm italic">
                The environments below represent contexts we envision. Renderings are conceptual.
              </span>
            </p>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--border-dark)] border border-[var(--border-dark)]">
          {ENVIRONMENTS.map((env, i) => (
            <motion.div
              key={env.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{
                duration: 0.7,
                delay: (i % 3) * 0.06,
                ease: [0.22, 1, 0.36, 1],
              }}
              className="group relative bg-[var(--black)] p-8 md:p-10 transition-colors duration-500 hover:bg-[var(--walnut-deep)]"
            >
              <div className="flex items-start justify-between mb-6">
                <span className="font-sans text-[10px] uppercase tracking-[0.42em] text-[var(--muted-dark)]">
                  0{i + 1}
                </span>
                <span className="h-px w-8 bg-[var(--gold)]/50 mt-2 transition-all duration-500 group-hover:w-14 group-hover:bg-[var(--gold-light)]" />
              </div>
              <h3 className="font-serif text-[var(--ivory)] text-2xl md:text-3xl tracking-tight mb-6 leading-snug">
                {env.name}
              </h3>
              <ul className="space-y-2">
                {env.contexts.map((c) => (
                  <li
                    key={c}
                    className="font-sans text-sm text-[var(--stone)]/80 leading-relaxed"
                  >
                    — {c}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
